import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import PageContainerHeader from "../../molecules/PageContainerHeader/PageContainerHeader";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import moment from "moment";
import { Tooltip } from "react-tooltip";
import { Modal, Button, Table, Space, Input } from "antd";
import "react-tooltip/dist/react-tooltip.css";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./DailyHoursPage.scss";
import ApplyHoursdrawer from "../../molecules/Apply-Hours-Drawer/Apply-Hours-Drawer";
import MonthlyAccessRequestModal, {
  ACCESS_SCOPE_CURRENT,
  ACCESS_SCOPE_PREVIOUS,
} from "../../molecules/MonthlyAccessRequestModal/MonthlyAccessRequestModal";
import DailyWorkUpdatesStats from "../../molecules/DailyWorkUpdatesStats/DailyWorkUpdatesStats";
import { getTasksByDateRangeAPI } from "../../../Redux/Apis/taskApi";
import { protectedApi } from "../../../app/utils/common/protectedApi";
import {
  APIS,
  DAILY_HOURS_ACCESS_PENDING_REFRESH_EVENT,
} from "../../../app/utils/constants/apiConstant";
import {
  getEmployeesAPI,
  getEmployeeDirectoryAPI,
} from "../../../Redux/Apis/employeeApi";
import { showErrorToast, showSuccessToast } from "../../../app/utils/common/Toast";
import {
  checkPermission,
  filterDailyWorkComplianceTeam,
  getDailyWorkComplianceTeamUserIds,
  hasAdminAccess,
  HR_ROLE_ID,
  permissionType,
} from "../../../app/utils/common/Functions";
import {
  formatComplianceNamesTooltip,
  getTeamDayComplianceByDate,
  userHasTasksOnDate,
} from "../../../app/utils/common/dailyWorkUpdateStats";
import {
  getLeaveCalendarTitle,
  getLeaveInfoForUserDate,
  getPartialLeaveComplianceStatus,
  getPartialLeaveStatusLabel,
  isFullLeaveDay,
  isPartialLeaveDay,
  isWfhDay,
  userMeetsDailyWorkRequirement,
} from "../../../app/utils/common/dailyWorkLeave";
import {
  canSubmitDailyWorkForDate,
  isDailyWorkUpdatePending,
} from "../../../app/utils/common/dailyWorkGracePeriod";
import {
  buildHolidayDateSet,
  getPublicHolidayCalendarTitle,
  getPublicHolidayInfo,
  isPublicHolidayDate,
} from "../../../app/utils/common/publicHolidays";
import {
  hasPendingMonthlyAccessRequest,
  readMonthlyAccessStatusCache,
  shouldFetchMonthlyAccessStatus,
  writeMonthlyAccessStatusCache,
} from "../../../app/utils/common/monthlyAccessStatusCache";

const DAILY_HOURS_EMPLOYEE_PAGE_SIZE = 500;
const DAILY_HOURS_ALL_TEAM = "all";
const DAILY_HOURS_EVENT_NAMES_TOOLTIP_ID = "daily-hours-event-names";
const TAB_CALENDAR = "calendar";
const TAB_ACCESS_REQUESTS = "access-requests";
const TAB_ACCESS_PENDING = "access-pending";
const TAB_ACCESS_HISTORY = "access-history";
const ACCESS_REVIEW_COMMENT_MIN_LENGTH = 4;
const ACCESS_REASON_MAX_LENGTH = 2000;
const ACCESS_REVIEW_POLL_MS = 30 * 1000;

/** Calendar event label; stacked leave + status, or team tooltip. */
const DailyHoursCalendarEvent = ({ event, title }) => {
  if (event?.leaveTitle && event?.statusTitle) {
    return (
      <span className="daily-hours-calendar-event-label daily-hours-calendar-event-label--stacked">
        <span className="daily-hours-calendar-event-label__leave">
          {event.leaveTitle}
        </span>
        <span
          className={`daily-hours-calendar-event-label__status daily-hours-calendar-event-label__status--${event.complianceStatus || ""}`}
        >
          {event.statusTitle}
        </span>
      </span>
    );
  }
  if (!event?.tooltip) {
    return <span>{title}</span>;
  }
  return (
    <span
      className="daily-hours-calendar-event-label"
      data-tooltip-id={DAILY_HOURS_EVENT_NAMES_TOOLTIP_ID}
      data-tooltip-content={event.tooltip}
    >
      {title}
    </span>
  );
};

const getMonthRange = (date) => {
  const m = moment(date);
  const monthEnd = m.clone().endOf("month");
  const capEnd = monthEnd.isAfter(moment(), "day") ? moment() : monthEnd;
  return {
    start: m.clone().startOf("month").format("YYYY-MM-DD"),
    end: capEnd.format("YYYY-MM-DD"),
  };
};

const DailyHoursPage = () => {
  const dispatch = useDispatch();
  const { calendarTasks, calendarLeaveByDate, calendarPublicHolidaysByDate, loading, isTaskUpdated } =
    useSelector((state) => state.task);
  const { user } = useSelector((state) => state.auth.auth);
  const isAdminRole = hasAdminAccess(user);
  const canReadAllDailyHours = checkPermission(
    permissionType.DAILY_HOURS,
    "read users"
  );
  const isAdminTeamStats = Boolean(
    user && isAdminRole && canReadAllDailyHours
  );
  const canReadUserMgmt = checkPermission(permissionType.USER_MANAGEMENT, "read");
  const localizer = momentLocalizer(moment);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewType, setViewType] = useState("month");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [teamRoster, setTeamRoster] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [sheetOpening, setSheetOpening] = useState(false);
  const [selectedTaskData, setSelectedTaskData] = useState(null);
  const [calendarHeight, setCalendarHeight] = useState(800);
  const [monthlyAccessStatus, setMonthlyAccessStatus] = useState(() =>
    readMonthlyAccessStatusCache()
  );
  const [monthlyAccessLoading, setMonthlyAccessLoading] = useState(false);
  const [accessAlertOpen, setAccessAlertOpen] = useState(false);
  const [accessRequestScope, setAccessRequestScope] = useState(
    ACCESS_SCOPE_PREVIOUS
  );
  const [activeMainTab, setActiveMainTab] = useState(TAB_CALENDAR);
  const [activeAccessSubTab, setActiveAccessSubTab] = useState(TAB_ACCESS_PENDING);
  const [reviewAccessRequests, setReviewAccessRequests] = useState([]);
  const [reviewAccessLoading, setReviewAccessLoading] = useState(false);
  const [reviewingAccessId, setReviewingAccessId] = useState(null);
  const [accessReviewModal, setAccessReviewModal] = useState(null);
  const [accessReviewComment, setAccessReviewComment] = useState("");

  const canReviewAccessRequests =
    isAdminRole || Number(user?.roleId) === HR_ROLE_ID;

  useEffect(() => {
    const updateCalendarHeight = () => {
      const width = window.innerWidth;
      if (width <= 767) {
        setCalendarHeight(460);
      } else if (width <= 1024) {
        setCalendarHeight(580);
      } else {
        setCalendarHeight(800);
      }
    };

    updateCalendarHeight();
    window.addEventListener("resize", updateCalendarHeight);
    return () => window.removeEventListener("resize", updateCalendarHeight);
  }, []);

  const loadMonthlyAccessStatus = useCallback(async ({ force = false, quiet = false } = {}) => {
    if (!user?.id) return;
    const cached = readMonthlyAccessStatusCache();
    if (!force && !shouldFetchMonthlyAccessStatus({ cached })) {
      if (cached) setMonthlyAccessStatus(cached);
      return;
    }
    if (!quiet) setMonthlyAccessLoading(true);
    try {
      const response = await protectedApi({
        url: APIS.TASK_MONTHLY_ACCESS_STATUS,
        method: "GET",
      });
      const data = response?.data?.data || null;
      setMonthlyAccessStatus(data);
      writeMonthlyAccessStatusCache(data);
    } catch (_error) {
      if (!quiet) setMonthlyAccessStatus(null);
    } finally {
      if (!quiet) setMonthlyAccessLoading(false);
    }
  }, [user?.id]);

  const hasPendingPreviousMonth = Boolean(monthlyAccessStatus?.hasPendingUpdates);
  const accessRequestStatus = String(
    monthlyAccessStatus?.requestStatus || ""
  ).toLowerCase();
  const hasPendingAccessRequest = accessRequestStatus === "pending";

  const canEditPreviousMonth = Boolean(
    monthlyAccessStatus?.canEditPreviousMonth ||
    (accessRequestStatus === "approved" && hasPendingPreviousMonth)
  );

  const hasCurrentMonthMissed = Boolean(
    monthlyAccessStatus?.hasCurrentMonthMissedUpdates
  );
  const currentMonthRequestStatus = String(
    monthlyAccessStatus?.currentMonthRequestStatus || ""
  ).toLowerCase();
  const hasPendingCurrentMonthAccessRequest =
    currentMonthRequestStatus === "pending";
  const canEditCurrentMonthMissed = Boolean(
    monthlyAccessStatus?.canEditCurrentMonthMissed ||
    (currentMonthRequestStatus === "approved" && hasCurrentMonthMissed)
  );

  const approvedPreviousMonthDates = useMemo(() => {
    const dates = Array.isArray(monthlyAccessStatus?.previousMonthApprovedDates)
      ? monthlyAccessStatus.previousMonthApprovedDates
      : [];
    return dates
      .map((d) => moment(d).format("YYYY-MM-DD"))
      .filter(Boolean);
  }, [monthlyAccessStatus?.previousMonthApprovedDates]);

  const approvedCurrentMonthDates = useMemo(() => {
    const dates = Array.isArray(monthlyAccessStatus?.currentMonthApprovedDates)
      ? monthlyAccessStatus.currentMonthApprovedDates
      : [];
    return dates
      .map((d) => moment(d).format("YYYY-MM-DD"))
      .filter(Boolean);
  }, [monthlyAccessStatus?.currentMonthApprovedDates]);

  const previousMonthUnapprovedCount = Array.isArray(
    monthlyAccessStatus?.previousMonthUnapprovedDates
  )
    ? monthlyAccessStatus.previousMonthUnapprovedDates.length
    : 0;
  const currentMonthUnapprovedDates = useMemo(
    () =>
      (Array.isArray(monthlyAccessStatus?.currentMonthUnapprovedDates)
        ? monthlyAccessStatus.currentMonthUnapprovedDates
        : []
      ).map((d) => moment(d).format("YYYY-MM-DD")),
    [monthlyAccessStatus?.currentMonthUnapprovedDates]
  );

  const needsAccessRequest =
    hasPendingPreviousMonth && previousMonthUnapprovedCount > 0;
  const isMonthlyBlocked = hasPendingPreviousMonth;

  const previousMonthLabel =
    monthlyAccessStatus?.previousMonth || "previous month";
  const currentMonthLabel =
    monthlyAccessStatus?.currentMonth || moment().format("MMMM YYYY");

  const holidayDates = useMemo(
    () => buildHolidayDateSet(calendarPublicHolidaysByDate),
    [calendarPublicHolidaysByDate]
  );

  const isDateAllowedForEntry = useCallback(
    (dateValue) => {
      const selectedDay = moment(dateValue).startOf("day");
      const dateStr = selectedDay.format("YYYY-MM-DD");
      if (isPublicHolidayDate(calendarPublicHolidaysByDate, dateStr)) return false;
      const isCurrentMonthDate = selectedDay.isSame(moment(), "month");

      if (isCurrentMonthDate) {
        if (hasPendingPreviousMonth) return false;
        if (canSubmitDailyWorkForDate(dateValue, moment(), holidayDates)) return true;
        return approvedCurrentMonthDates.includes(dateStr);
      }

      if (canSubmitDailyWorkForDate(dateValue, moment(), holidayDates)) return true;
      return approvedPreviousMonthDates.includes(dateStr);
    },
    [
      calendarPublicHolidaysByDate,
      holidayDates,
      hasPendingPreviousMonth,
      approvedPreviousMonthDates,
      approvedCurrentMonthDates,
    ]
  );

  const loadReviewAccessRequests = useCallback(async () => {
    if (!canReviewAccessRequests) return;
    setReviewAccessLoading(true);
    try {
      const response = await protectedApi({
        url: `${APIS.TASK_MONTHLY_ACCESS_HISTORY_REVIEW}?limit=300`,
        method: "GET",
      });
      const all = Array.isArray(response?.data?.data) ? response.data.data : [];
      setReviewAccessRequests(all);
    } catch (err) {
      setReviewAccessRequests([]);
      const message =
        err?.response?.data?.error?.message ||
        err?.response?.data?.error ||
        "Unable to load access requests.";
      showErrorToast(typeof message === "string" ? message : "Unable to load access requests.");
    } finally {
      setReviewAccessLoading(false);
    }
  }, [canReviewAccessRequests]);

  const pendingAccessRequests = useMemo(
    () =>
      reviewAccessRequests.filter(
        (r) => String(r?.status || "").toLowerCase() === "pending"
      ),
    [reviewAccessRequests]
  );

  const accessRequestHistory = useMemo(
    () =>
      reviewAccessRequests.filter(
        (r) => String(r?.status || "").toLowerCase() !== "pending"
      ),
    [reviewAccessRequests]
  );

  useEffect(() => {
    if (!canReviewAccessRequests) return;
    loadReviewAccessRequests();
  }, [canReviewAccessRequests, loadReviewAccessRequests]);

  useEffect(() => {
    if (activeMainTab === TAB_ACCESS_REQUESTS && canReviewAccessRequests) {
      loadReviewAccessRequests();
    }
  }, [activeMainTab, canReviewAccessRequests, loadReviewAccessRequests]);

  const openAccessReviewModal = useCallback((row, status) => {
    setAccessReviewModal({ requestId: row.requestId, status, employeeName: row.employeeName });
    setAccessReviewComment("");
  }, []);

  const closeAccessReviewModal = useCallback(() => {
    setAccessReviewModal(null);
    setAccessReviewComment("");
  }, []);

  const reviewAccessRequest = useCallback(
    async (requestId, status, reviewComment) => {
      const trimmedComment = String(reviewComment || "").trim();
      if (trimmedComment.length < ACCESS_REVIEW_COMMENT_MIN_LENGTH) {
        showErrorToast(
          `Please enter a review comment of at least ${ACCESS_REVIEW_COMMENT_MIN_LENGTH} characters.`
        );
        return;
      }
      setReviewingAccessId(requestId);
      try {
        const response = await protectedApi({
          url: APIS.TASK_MONTHLY_ACCESS_REVIEW.replace(":requestId", String(requestId)),
          method: "PUT",
          data: { status, reviewComment: trimmedComment },
        });
        showSuccessToast(
          response?.data?.message || (status === "approved" ? "Approved." : "Rejected.")
        );
        closeAccessReviewModal();
        window.dispatchEvent(new Event(DAILY_HOURS_ACCESS_PENDING_REFRESH_EVENT));
        await loadReviewAccessRequests();
      } catch (err) {
        const message =
          err?.response?.data?.error?.message ||
          err?.response?.data?.error ||
          "Unable to review access request.";
        showErrorToast(typeof message === "string" ? message : "Unable to review access request.");
      } finally {
        setReviewingAccessId(null);
      }
    },
    [closeAccessReviewModal, loadReviewAccessRequests]
  );

  // Ref to track the last API call to prevent duplicates
  const lastApiCallRef = useRef({
    startDate: null,
    endDate: null,
    timestamp: 0,
  });

  const viewingAllTeam =
    isAdminTeamStats && String(selectedEmployee) === DAILY_HOURS_ALL_TEAM;
  const calendarUserId = viewingAllTeam
    ? ""
    : String(selectedEmployee ?? user?.id ?? "");

  const isOwnDailyHoursView = useMemo(
    () =>
      !viewingAllTeam &&
      (!selectedEmployee || String(selectedEmployee) === String(user?.id)),
    [viewingAllTeam, selectedEmployee, user?.id]
  );

  useEffect(() => {
    if (!user?.id || !isOwnDailyHoursView) return;
    loadMonthlyAccessStatus();
  }, [user?.id, isOwnDailyHoursView, loadMonthlyAccessStatus]);

  const isAwaitingAccessReview = hasPendingMonthlyAccessRequest(monthlyAccessStatus);

  useEffect(() => {
    if (!user?.id || !isOwnDailyHoursView || !isAwaitingAccessReview) {
      return undefined;
    }
    const refresh = () => {
      if (document.visibilityState === "hidden") return;
      loadMonthlyAccessStatus({ force: true, quiet: true });
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    const intervalId = setInterval(refresh, ACCESS_REVIEW_POLL_MS);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
      clearInterval(intervalId);
    };
  }, [user?.id, isOwnDailyHoursView, isAwaitingAccessReview, loadMonthlyAccessStatus]);

  const openMonthlyAccessAlert = useCallback(
    (scope = ACCESS_SCOPE_PREVIOUS) => {
      setAccessRequestScope(
        scope === ACCESS_SCOPE_CURRENT
          ? ACCESS_SCOPE_CURRENT
          : ACCESS_SCOPE_PREVIOUS
      );
      setAccessAlertOpen(true);
    },
    []
  );

  const handleMonthlyAccessStatusChange = useCallback((data) => {
    setMonthlyAccessStatus(data);
    writeMonthlyAccessStatusCache(data);
  }, []);

  const goToPreviousMonthOnCalendar = useCallback(() => {
    setCurrentDate(moment().subtract(1, "month").startOf("month").toDate());
    setAccessAlertOpen(false);
  }, []);

  const autoNavigatedToPreviousMonthRef = useRef(false);

  /** Show access-request popup whenever previous-month pending blocks editing. */
  const tryShowMonthlyAccessAlert = useCallback(() => {
    if (!isOwnDailyHoursView || !needsAccessRequest) return false;
    openMonthlyAccessAlert(ACCESS_SCOPE_PREVIOUS);
    return true;
  }, [isOwnDailyHoursView, needsAccessRequest, openMonthlyAccessAlert]);

  /** Returns true when entry was blocked and the approval alert was shown. */
  const tryBlockCurrentMonthEntry = useCallback(
    (dateValue) => {
      if (!isOwnDailyHoursView || !isMonthlyBlocked) return false;
      if (!moment(dateValue).isSame(moment(), "month")) return false;
      openMonthlyAccessAlert(ACCESS_SCOPE_PREVIOUS);
      return true;
    },
    [isOwnDailyHoursView, isMonthlyBlocked, openMonthlyAccessAlert]
  );

  useEffect(() => {
    if (!canEditPreviousMonth) {
      autoNavigatedToPreviousMonthRef.current = false;
      return;
    }
    if (!isOwnDailyHoursView) return;
    if (autoNavigatedToPreviousMonthRef.current) return;
    autoNavigatedToPreviousMonthRef.current = true;
    setCurrentDate(moment().subtract(1, "month").startOf("month").toDate());
  }, [isOwnDailyHoursView, canEditPreviousMonth]);

  const teamUserIds = useMemo(
    () => getDailyWorkComplianceTeamUserIds(teamRoster),
    [teamRoster]
  );

  const canPickEmployees = checkPermission(
    permissionType.DAILY_HOURS,
    "read users"
  );

  const employeeSelectOptions = useMemo(() => {
    const teamOpts = teamRoster
      .map((emp) => {
        const name = [emp.firstname, emp.lastname].filter(Boolean).join(" ");
        const roleLabel = emp.role ?? emp.roleName ?? "";
        return {
          value: emp.userId ?? emp.id,
          label: roleLabel ? `${name} (${roleLabel})` : name,
        };
      })
      .sort((a, b) =>
        String(a.label).localeCompare(String(b.label), undefined, {
          sensitivity: "base",
        })
      );

    if (isAdminTeamStats) {
      return [{ value: DAILY_HOURS_ALL_TEAM, label: "All team" }, ...teamOpts];
    }
    return teamOpts;
  }, [teamRoster, isAdminTeamStats]);

  const selectedEmployeeProfile = useMemo(() => {
    if (viewingAllTeam || !selectedEmployee) return null;
    return teamRoster.find(
      (emp) =>
        String(emp?.userId ?? emp?.id ?? "") === String(selectedEmployee)
    );
  }, [viewingAllTeam, selectedEmployee, teamRoster]);

  const selectedEmployeeName = useMemo(() => {
    if (!selectedEmployeeProfile) return "";
    const name = [
      selectedEmployeeProfile.firstname,
      selectedEmployeeProfile.lastname,
    ]
      .filter(Boolean)
      .join(" ")
      .trim();
    return name || selectedEmployeeProfile.email || "";
  }, [selectedEmployeeProfile]);

  const dayIsCompliantForCalendarUser = useCallback(
    (dateStr) => {
      const entries = calendarTasks?.[dateStr];
      if (viewingAllTeam) {
        if (!teamUserIds.length) {
          return userMeetsDailyWorkRequirement(entries, null, null);
        }
        return teamUserIds.every((id) => {
          const leaveInfo = getLeaveInfoForUserDate(
            calendarLeaveByDate,
            dateStr,
            id
          );
          return userMeetsDailyWorkRequirement(entries, id, leaveInfo);
        });
      }
      const leaveInfo = getLeaveInfoForUserDate(
        calendarLeaveByDate,
        dateStr,
        calendarUserId
      );
      return userMeetsDailyWorkRequirement(
        entries,
        calendarUserId,
        leaveInfo
      );
    },
    [
      calendarTasks,
      calendarLeaveByDate,
      calendarUserId,
      viewingAllTeam,
      teamUserIds,
    ]
  );

  const dayHasPendingForCalendarUser = useCallback(
    (dateStr) => !dayIsCompliantForCalendarUser(dateStr),
    [dayIsCompliantForCalendarUser]
  );

  const isLockedMissedDayForUser = useCallback(
    (dateValue) => {
      const dateStr = moment(dateValue).format("YYYY-MM-DD");
      const day = moment(dateStr);
      if (!day.isValid()) return false;
      if (day.day() === 0 || day.day() === 6) return false;
      if (isPublicHolidayDate(calendarPublicHolidaysByDate, dateStr)) return false;
      if (!day.isSame(moment(), "month")) return false;
      if (day.isSame(moment(), "day")) return false;
      if (dayIsCompliantForCalendarUser(dateStr)) return false;
      if (isDailyWorkUpdatePending(dateStr, moment(), holidayDates)) return false;
      return true;
    },
    [
      calendarPublicHolidaysByDate,
      dayIsCompliantForCalendarUser,
      holidayDates,
    ]
  );

  const currentMonthLockedMissedDates = useMemo(() => {
    if (!isOwnDailyHoursView || viewingAllTeam) return [];
    const dates = [];
    const monthStart = moment().startOf("month");
    const yesterday = moment().subtract(1, "day").startOf("day");
    for (
      let m = moment(monthStart);
      m.isSameOrBefore(yesterday, "day");
      m.add(1, "day")
    ) {
      const dateStr = m.format("YYYY-MM-DD");
      if (isLockedMissedDayForUser(dateStr)) dates.push(dateStr);
    }
    return dates;
  }, [isOwnDailyHoursView, viewingAllTeam, isLockedMissedDayForUser]);

  const hasCurrentMonthMissedOnCalendar = currentMonthLockedMissedDates.some(
    (d) => !approvedCurrentMonthDates.includes(d)
  );

  const isCalendarLoading =
    loading || monthlyAccessLoading || (viewingAllTeam && rosterLoading);

  const needsCurrentMonthAccessRequest =
    !hasPendingPreviousMonth &&
    (isCalendarLoading
      ? currentMonthUnapprovedDates.length > 0
      : hasCurrentMonthMissedOnCalendar);

  const shouldPromptCurrentMonthAccessForDate = useCallback(
    (dateStr) => {
      if (!dateStr || !needsCurrentMonthAccessRequest) return false;
      if (approvedCurrentMonthDates.includes(dateStr)) return false;
      if (isCalendarLoading) {
        return currentMonthUnapprovedDates.includes(dateStr);
      }
      return isLockedMissedDayForUser(dateStr);
    },
    [
      needsCurrentMonthAccessRequest,
      approvedCurrentMonthDates,
      isCalendarLoading,
      currentMonthUnapprovedDates,
      isLockedMissedDayForUser,
    ]
  );

  /** Show popup for locked missed days in the current month (only for that date). */
  const tryShowCurrentMonthAccessAlert = useCallback(
    (dateStr) => {
      if (!dateStr) return false;
      if (!shouldPromptCurrentMonthAccessForDate(dateStr)) return false;
      openMonthlyAccessAlert(ACCESS_SCOPE_CURRENT);
      return true;
    },
    [shouldPromptCurrentMonthAccessForDate, openMonthlyAccessAlert]
  );

  const getCalendarViewRange = useCallback(() => {
    if (!currentDate) return null;

    const effectiveViewType = viewType || "month";
    let startDate;
    let endDate;

    if (effectiveViewType === "month") {
      startDate = moment(currentDate).startOf("month");
      endDate = moment(currentDate).endOf("month");
    } else if (effectiveViewType === "week") {
      startDate = moment(currentDate).startOf("week");
      endDate = moment(currentDate).endOf("week");
    } else if (effectiveViewType === "day") {
      startDate = moment(currentDate).startOf("day");
      endDate = moment(currentDate).endOf("day");
    } else {
      return null;
    }

    const today = moment().endOf("day");
    if (endDate.isAfter(today)) {
      endDate = today;
    }

    return { startDate, endDate };
  }, [currentDate, viewType]);

  // Format tasks for the calendar
  const calendarEvents = useMemo(() => {
    if (!calendarTasks) return [];

    const events = [];
    const viewRange = getCalendarViewRange();

    const pushWorkdayEventsForRange = (buildEventsForDate) => {
      if (!viewRange) return;
      const { startDate, endDate } = viewRange;

      for (
        let m = moment(startDate);
        m.isSameOrBefore(endDate);
        m.add(1, "days")
      ) {
        const dayOfWeek = m.day();
        if (dayOfWeek === 0 || dayOfWeek === 6) continue;
        buildEventsForDate(m.format("YYYY-MM-DD"), m.toDate());
      }
    };

    if (viewingAllTeam) {
      if (!teamRoster.length) return events;

      pushWorkdayEventsForRange((dateStr, dayDate) => {
        const holidayInfo = getPublicHolidayInfo(calendarPublicHolidaysByDate, dateStr);
        if (holidayInfo) {
          events.push({
            id: `${dateStr}-public-holiday`,
            title: getPublicHolidayCalendarTitle(holidayInfo),
            start: dayDate,
            end: dayDate,
            allDay: true,
            resource: { date: dateStr, users: [], holidayInfo },
            className: "public-holiday-event",
          });
          return;
        }

        const { filled, missed, pending } = getTeamDayComplianceByDate({
          calendarTasks,
          calendarLeaveByDate,
          publicHolidaysByDate: calendarPublicHolidaysByDate,
          dateStr,
          teamProfiles: teamRoster,
        });
        const userEntries = Array.isArray(calendarTasks[dateStr])
          ? calendarTasks[dateStr]
          : [];

        if (filled.length) {
          events.push({
            id: `${dateStr}-filled`,
            title: "Filled",
            tooltip: formatComplianceNamesTooltip(filled),
            start: dayDate,
            end: dayDate,
            allDay: true,
            resource: {
              date: dateStr,
              users: userEntries.filter((entry) =>
                filled.some(
                  (p) =>
                    String(entry?.userDetails?.userId ?? "") === String(p.id)
                )
              ),
            },
          });
        }

        if (missed.length) {
          events.push({
            id: `${dateStr}-missed`,
            title: "Missed",
            tooltip: formatComplianceNamesTooltip(missed),
            start: dayDate,
            end: dayDate,
            allDay: true,
            resource: { date: dateStr, users: [] },
            className: "missed-event",
          });
        }

        if (pending.length) {
          events.push({
            id: `${dateStr}-pending`,
            title: "Pending",
            tooltip: formatComplianceNamesTooltip(pending),
            start: dayDate,
            end: dayDate,
            allDay: true,
            resource: { date: dateStr, users: [] },
            className: "pending-event",
          });
        }
      });

      return events;
    }

    pushWorkdayEventsForRange((dateStr, dayDate) => {
      const holidayInfo = getPublicHolidayInfo(calendarPublicHolidaysByDate, dateStr);
      if (holidayInfo) {
        events.push({
          id: `${dateStr}-public-holiday`,
          title: getPublicHolidayCalendarTitle(holidayInfo),
          start: dayDate,
          end: dayDate,
          allDay: true,
          resource: { date: dateStr, users: [], holidayInfo },
          className: "public-holiday-event",
        });
        return;
      }

      const entries = calendarTasks?.[dateStr];
      const leaveInfo = getLeaveInfoForUserDate(
        calendarLeaveByDate,
        dateStr,
        calendarUserId
      );
      const compliant = dayIsCompliantForCalendarUser(dateStr);
      const hasTasks = userHasTasksOnDate(entries, calendarUserId);
      const users = Array.isArray(entries) ? entries : [];
      const filteredUsers = users.filter(
        (entry) =>
          String(entry?.userDetails?.userId ?? "") === calendarUserId
      );

      const isToday = moment(dateStr).isSame(moment(), "day");
      const isPendingDay = isDailyWorkUpdatePending(dateStr, moment(), holidayDates);
      const complianceStatus = getPartialLeaveComplianceStatus({
        isCompliant: compliant,
        isPending: isPendingDay,
      });

      if (!compliant && isToday) {
        return;
      }

      if (compliant) {
        if (!hasTasks && isFullLeaveDay(leaveInfo)) {
          events.push({
            id: dateStr,
            title: getLeaveCalendarTitle(leaveInfo),
            start: dayDate,
            end: dayDate,
            allDay: true,
            resource: {
              date: dateStr,
              users: filteredUsers,
              leaveInfo,
              complianceStatus,
            },
            className: "on-leave-event",
          });
          return;
        }

        if (isPartialLeaveDay(leaveInfo)) {
          const statusTitle = getPartialLeaveStatusLabel("filled");
          events.push({
            id: dateStr,
            title: statusTitle,
            leaveTitle: getLeaveCalendarTitle(leaveInfo),
            statusTitle,
            complianceStatus: "filled",
            start: dayDate,
            end: dayDate,
            allDay: true,
            resource: {
              date: dateStr,
              users: filteredUsers,
              leaveInfo,
              complianceStatus: "filled",
            },
            className: "leave-partial-stacked-event",
          });
          return;
        }

        if (isWfhDay(leaveInfo)) {
          const statusTitle = hasTasks ? "Filled" : getPartialLeaveStatusLabel("filled");
          events.push({
            id: dateStr,
            title: statusTitle,
            leaveTitle: getLeaveCalendarTitle(leaveInfo),
            statusTitle,
            complianceStatus: "filled",
            start: dayDate,
            end: dayDate,
            allDay: true,
            resource: {
              date: dateStr,
              users: filteredUsers,
              leaveInfo,
              complianceStatus: "filled",
            },
            className: "wfh-stacked-event",
          });
          return;
        }

        events.push({
          id: dateStr,
          title: "Filled",
          start: dayDate,
          end: dayDate,
          allDay: true,
          resource: {
            date: dateStr,
            users: filteredUsers,
            leaveInfo,
            complianceStatus,
          },
        });
        return;
      }

      if (isPartialLeaveDay(leaveInfo)) {
        const statusTitle = getPartialLeaveStatusLabel(complianceStatus);
        events.push({
          id: dateStr + (isPendingDay ? "-pending" : "-missed"),
          title: statusTitle,
          leaveTitle: getLeaveCalendarTitle(leaveInfo),
          statusTitle,
          complianceStatus,
          start: dayDate,
          end: dayDate,
          allDay: true,
          resource: {
            date: dateStr,
            users: [],
            leaveInfo,
            complianceStatus,
          },
          className: "leave-partial-stacked-event",
        });
        return;
      }

      if (isWfhDay(leaveInfo)) {
        const statusTitle = getPartialLeaveStatusLabel(complianceStatus);
        events.push({
          id: dateStr + (isPendingDay ? "-pending" : "-missed"),
          title: statusTitle,
          leaveTitle: getLeaveCalendarTitle(leaveInfo),
          statusTitle,
          complianceStatus,
          start: dayDate,
          end: dayDate,
          allDay: true,
          resource: {
            date: dateStr,
            users: [],
            leaveInfo,
            complianceStatus,
          },
          className: "wfh-stacked-event",
        });
        return;
      }

      const title = isPendingDay ? "Pending" : "Missed";
      events.push({
        id: dateStr + (isPendingDay ? "-pending" : "-missed"),
        title,
        start: dayDate,
        end: dayDate,
        allDay: true,
        resource: {
          date: dateStr,
          users: [],
          leaveInfo,
          complianceStatus,
        },
        className: isPendingDay ? "pending-event" : "missed-event",
      });
    });

    return events;
  }, [
    calendarTasks,
    calendarLeaveByDate,
    calendarPublicHolidaysByDate,
    holidayDates,
    calendarUserId,
    viewingAllTeam,
    teamRoster,
    getCalendarViewRange,
    dayIsCompliantForCalendarUser,
  ]);

  useEffect(() => {
    if (!canPickEmployees) return;
    let cancelled = false;
    setRosterLoading(true);

    const loadRoster = async () => {
      try {
        let roster = [];
        if (canReadUserMgmt) {
          const res = await dispatch(
            getEmployeesAPI({
              page: 1,
              limit: DAILY_HOURS_EMPLOYEE_PAGE_SIZE,
              filterBy: "active",
            })
          ).unwrap();
          roster = res?.data?.userData ?? [];
        } else {
          const res = await dispatch(getEmployeeDirectoryAPI()).unwrap();
          roster = res?.data?.users ?? [];
        }
        if (!cancelled) {
          setTeamRoster(filterDailyWorkComplianceTeam(roster));
        }
      } catch {
        if (!cancelled) setTeamRoster([]);
      } finally {
        if (!cancelled) setRosterLoading(false);
      }
    };

    loadRoster();
    return () => {
      cancelled = true;
    };
  }, [dispatch, canPickEmployees, canReadUserMgmt]);

  const resolveTaskFetchUserId = useCallback(
    (id) => {
      const pick = id ?? selectedEmployee ?? user?.id;
      if (isAdminTeamStats && String(pick) === DAILY_HOURS_ALL_TEAM) {
        return "all";
      }
      return pick;
    },
    [isAdminTeamStats, selectedEmployee, user?.id]
  );

  const fetchTasksByRange = useCallback(
    (startDate, endDate, id = selectedEmployee, { force = false } = {}) => {
      const fetchId = resolveTaskFetchUserId(id);
      const range =
        startDate && endDate
          ? { start: startDate, end: endDate }
          : getMonthRange(currentDate);
      const { start, end } = range;
      const adjustedEndDate = moment(end).isAfter(moment(), "day")
        ? moment().format("YYYY-MM-DD")
        : end;

      const now = Date.now();
      const isSameRange =
        lastApiCallRef.current.startDate === start &&
        lastApiCallRef.current.endDate === adjustedEndDate &&
        lastApiCallRef.current.fetchId === String(fetchId);
      const isRecentCall = now - lastApiCallRef.current.timestamp < 1000;

      if (!force && startDate && endDate && isSameRange && isRecentCall) {
        return Promise.resolve();
      }

      lastApiCallRef.current = {
        startDate: start,
        endDate: adjustedEndDate,
        fetchId: String(fetchId),
        timestamp: now,
      };

      return dispatch(
        getTasksByDateRangeAPI({
          startDate: start,
          endDate: adjustedEndDate,
          id: fetchId,
        })
      ).unwrap();
    },
    [dispatch, resolveTaskFetchUserId, currentDate]
  );

  const refreshCalendarAndAccess = useCallback(async () => {
    if (!user?.id || selectedEmployee == null) return;
    const { start, end } = getMonthRange(currentDate);
    try {
      await fetchTasksByRange(start, end, selectedEmployee, { force: true });
    } catch {
      // Errors are surfaced by the task reducer.
    }
    if (isOwnDailyHoursView) {
      await loadMonthlyAccessStatus({ force: true });
    }
  }, [
    user?.id,
    selectedEmployee,
    currentDate,
    fetchTasksByRange,
    isOwnDailyHoursView,
    loadMonthlyAccessStatus,
  ]);

  useEffect(() => {
    if (!user?.id) return;
    setSelectedEmployee((prev) => {
      if (prev != null) return prev;
      return isAdminTeamStats ? DAILY_HOURS_ALL_TEAM : user.id;
    });
  }, [user?.id, isAdminTeamStats]);

  useEffect(() => {
    if (!user?.id || selectedEmployee == null) return;
    const { start, end } = getMonthRange(currentDate);
    fetchTasksByRange(start, end);
  }, [user?.id, selectedEmployee, currentDate, fetchTasksByRange]);

  useEffect(() => {
    if (!isTaskUpdated) return;
    refreshCalendarAndAccess();
  }, [isTaskUpdated, refreshCalendarAndAccess]);

  const handleNavigate = useCallback((newDate) => {
    setCurrentDate(newDate);
  }, []);

  const toggleHoursDrawer = (slotInfo) => {
    if (viewingAllTeam) return;

    // Closing the drawer (no slot) — keep existing close behavior.
    if (!slotInfo?.start) {
      setSelectedDate(new Date());
      setIsReadOnly(false);
      setSelectedTaskData([]);
      setIsDrawerOpen(false);
      return;
    }

    const dateStr = moment(slotInfo.start).format("YYYY-MM-DD");
    // Company public holidays are non-working — never open entry or access modals.
    if (holidayDates.has(dateStr) || isPublicHolidayDate(calendarPublicHolidaysByDate, dateStr)) {
      return;
    }

    if (
      checkPermission(permissionType.DAILY_HOURS, "create") &&
      (!selectedEmployee || selectedEmployee === user?.id)
    ) {
      if (tryBlockCurrentMonthEntry(slotInfo.start)) {
        return;
      }

      if (dayIsCompliantForCalendarUser(dateStr)) {
        setSelectedDate(slotInfo.start);
        setIsReadOnly(true);
        setSelectedTaskData(calendarTasks?.[dateStr] || []);
        setIsDrawerOpen(true);
        return;
      }

      if (isDateAllowedForEntry(slotInfo.start)) {
        if (
          calendarTasks &&
          calendarTasks[dateStr] &&
          calendarTasks[dateStr].length > 0
        ) {
          setSelectedDate(slotInfo.start);
          setIsReadOnly(true);
          setSelectedTaskData(calendarTasks[dateStr]);
          setIsDrawerOpen(true);
        } else {
          setSelectedDate(slotInfo.start);
          setIsReadOnly(false);
          setSelectedTaskData([]);
          setIsDrawerOpen(true);
        }
      } else if (tryShowMonthlyAccessAlert()) {
        return;
      } else if (tryShowCurrentMonthAccessAlert(dateStr)) {
        return;
      } else if (shouldPromptCurrentMonthAccessForDate(dateStr)) {
        openMonthlyAccessAlert(ACCESS_SCOPE_CURRENT);
        return;
      } else {
        showErrorToast(
          "You can only log hours for today, the next working day after a missed day (e.g. Friday on Monday), or approved access dates."
        );
      }
    }
  };

  const handleEventClick = (event) => {
    if (viewingAllTeam) return;

    const dateStr = event.resource?.date
      ? moment(event.resource.date).format("YYYY-MM-DD")
      : moment(event.start).format("YYYY-MM-DD");

    if (
      event.resource?.holidayInfo ||
      event.className === "public-holiday-event" ||
      holidayDates.has(dateStr) ||
      isPublicHolidayDate(calendarPublicHolidaysByDate, dateStr)
    ) {
      return;
    }

    const users = event.resource.users || []; // Ensure users is at least an empty array

    const leaveInfo = event.resource?.leaveInfo;
    const complianceStatus = event.resource?.complianceStatus;
    const isLeaveOnlyDay =
      isFullLeaveDay(leaveInfo) && !(users?.length && users[0]?.taskDetails?.length);
    const isPartialLeaveEvent = isPartialLeaveDay(leaveInfo);
    const isWfhEvent = isWfhDay(leaveInfo);
    const isStackedWorkEvent = isPartialLeaveEvent || isWfhEvent;
    const isStackedIncomplete =
      isStackedWorkEvent && complianceStatus !== "filled";
    const isStandardMissed =
      event.title === "Missed" || event.title === "Pending";

    if (isStandardMissed && !isStackedIncomplete && !isDateAllowedForEntry(dateStr)) {
      if (dayIsCompliantForCalendarUser(dateStr)) {
        setSelectedTaskData(users?.length ? users : calendarTasks?.[dateStr] || []);
        setSelectedDate(new Date(dateStr));
        setIsReadOnly(true);
        setIsDrawerOpen(true);
        return;
      }
      if (
        tryShowMonthlyAccessAlert() ||
        tryBlockCurrentMonthEntry(dateStr) ||
        tryShowCurrentMonthAccessAlert(dateStr)
      ) {
        return;
      }
      if (shouldPromptCurrentMonthAccessForDate(dateStr)) {
        openMonthlyAccessAlert(ACCESS_SCOPE_CURRENT);
      }
      return;
    }

    if (
      (event.title === "Pending" || isStackedIncomplete) &&
      (!selectedEmployee || selectedEmployee === user?.id)
    ) {
      if (tryBlockCurrentMonthEntry(dateStr)) return;
      if (!isDateAllowedForEntry(dateStr) && isStandardMissed) {
        if (
          tryShowMonthlyAccessAlert() ||
          tryShowCurrentMonthAccessAlert(dateStr)
        ) {
          return;
        }
        return;
      }
      setSelectedDate(new Date(dateStr));
      setIsReadOnly(false);
      setSelectedTaskData(
        users?.length ? users : calendarTasks?.[dateStr] || []
      );
      setIsDrawerOpen(true);
      return;
    }
    if (
      (event.title === "Missed" || isStackedIncomplete) &&
      (!selectedEmployee || selectedEmployee === user?.id) &&
      isDateAllowedForEntry(dateStr)
    ) {
      if (tryBlockCurrentMonthEntry(dateStr)) return;
      setSelectedDate(new Date(dateStr));
      setIsReadOnly(false);
      setSelectedTaskData(
        users?.length ? users : calendarTasks?.[dateStr] || []
      );
      setIsDrawerOpen(true);
      return;
    }
    if (
      event.title === "Filled" ||
      isLeaveOnlyDay ||
      (isStackedWorkEvent && complianceStatus === "filled")
    ) {
      setSelectedTaskData(users?.length ? users : []);
      setSelectedDate(new Date(dateStr));
      setIsReadOnly(true);
      setIsDrawerOpen(true);
      return;
    }
  };

  const defaultEmployeeSelection = useMemo(
    () => (isAdminTeamStats ? DAILY_HOURS_ALL_TEAM : user?.id),
    [isAdminTeamStats, user?.id]
  );

  const onSelectEmployee = (value) => {
    setSelectedEmployee(value ?? defaultEmployeeSelection);
  };

  const clearEmployeeSelection = useCallback(() => {
    setSelectedEmployee(defaultEmployeeSelection);
  }, [defaultEmployeeSelection]);
  const statsMonth = useMemo(() => moment(currentDate), [currentDate]);

  const handleViewSheet = async () => {
    if (viewingAllTeam) {
      showErrorToast("Select an employee to view their Google Sheet.");
      return;
    }
    const userId = selectedEmployee ?? user?.id;
    if (!userId) {
      showErrorToast("Select an employee to view their Google Sheet.");
      return;
    }

    setSheetOpening(true);
    try {
      const response = await protectedApi({
        url: `${APIS.TASK_EMPLOYEE_SPREADSHEET}/${userId}`,
        method: "GET",
      });
      const url = response?.data?.data?.url;
      if (!url) {
        showErrorToast("Google Sheet is not available for this employee.");
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      const message =
        err?.response?.data?.error?.message ||
        err?.response?.data?.error ||
        "Unable to open Google Sheet.";
      showErrorToast(
        typeof message === "string" ? message : "Unable to open Google Sheet."
      );
    } finally {
      setSheetOpening(false);
    }
  };

  const accessRequestSubTabs = useMemo(
    () => [
      {
        key: TAB_ACCESS_PENDING,
        label: "Pending requests",
        badgeCount: pendingAccessRequests.length,
      },
      { key: TAB_ACCESS_HISTORY, label: "Request history" },
    ],
    [pendingAccessRequests.length]
  );

  const renderAccessRequestsPanel = () => (
    <div
      className={`daily-hours-access-panel card-border${pendingAccessRequests.length > 0 ? " daily-hours-access-panel--highlight" : ""
        }`}
    >
      <p className="daily-hours-access-panel__intro">
        Employees can request temporary edit access for incomplete previous-month
        updates, or for current-month days that are past the next-working-day entry
        window. Approve to let them complete pending dates.
      </p>
      <div
        className="daily-hours-access-sub-tabs"
        role="tablist"
        aria-label="Access request views"
      >
        {accessRequestSubTabs.map((tab) => {
          const isActive = activeAccessSubTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`daily-hours-access-sub-tabs__item${isActive ? " is-active" : ""
                }`}
              onClick={() => setActiveAccessSubTab(tab.key)}
            >
              <span className="daily-hours-access-sub-tabs__label">{tab.label}</span>
              {tab.badgeCount > 0 ? (
                <span className="daily-hours-access-sub-tabs__badge">
                  {tab.badgeCount > 99 ? "99+" : tab.badgeCount}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      <div className="daily-hours-access-panel__table-wrap">
        {activeAccessSubTab === TAB_ACCESS_PENDING ? (
          <Table
            size="small"
            rowKey="requestId"
            loading={reviewAccessLoading}
            dataSource={pendingAccessRequests}
            pagination={false}
            scroll={{ x: 900 }}
            locale={{ emptyText: "No pending requests." }}
            columns={[
              { title: "Employee", dataIndex: "employeeName", key: "employeeName" },
              {
                title: "Employee ID",
                dataIndex: "employeeId",
                key: "employeeId",
                render: (id) => id || "—",
              },
              {
                title: "Target month",
                dataIndex: "targetMonth",
                key: "targetMonth",
                render: (label, row) =>
                  label || row.previousMonth || "—",
              },
              {
                title: "Pending dates",
                dataIndex: "pendingDates",
                key: "pendingDates",
                render: (dates) => {
                  if (!Array.isArray(dates) || !dates.length) return "—";
                  return dates.join(", ");
                },
              },
              {
                title: "Reason (missed updates)",
                dataIndex: "requestReason",
                key: "requestReason",
                width: 220,
                render: (reason) => reason || "—",
              },
              {
                title: "Requested",
                dataIndex: "requestedAt",
                key: "requestedAt",
                render: (t) => (t ? moment(t).format("D MMM YYYY, h:mm A") : "—"),
              },
              {
                title: "Actions",
                key: "actions",
                width: 180,
                render: (_, row) => (
                  <Space size="small" wrap>
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => openAccessReviewModal(row, "approved")}
                    >
                      Approve
                    </Button>
                    <Button
                      danger
                      size="small"
                      onClick={() => openAccessReviewModal(row, "rejected")}
                    >
                      Reject
                    </Button>
                  </Space>
                ),
              },
            ]}
          />
        ) : (
          <Table
            size="small"
            rowKey="requestId"
            loading={reviewAccessLoading}
            dataSource={accessRequestHistory}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            scroll={{ x: 900 }}
            locale={{ emptyText: "No reviewed requests yet." }}
            columns={[
              { title: "Employee", dataIndex: "employeeName", key: "employeeName" },
              {
                title: "Employee ID",
                dataIndex: "employeeId",
                key: "employeeId",
                render: (id) => id || "—",
              },
              {
                title: "Target month",
                dataIndex: "targetMonth",
                key: "targetMonth",
                render: (label, row) =>
                  label || row.previousMonth || "—",
              },
              {
                title: "Status",
                dataIndex: "status",
                key: "status",
                render: (s) => (s ? String(s).toUpperCase() : "—"),
              },
              {
                title: "Reason (missed updates)",
                dataIndex: "requestReason",
                key: "requestReason",
                width: 200,
                render: (reason) => reason || "—",
              },
              {
                title: "Review comment",
                dataIndex: "reviewComment",
                key: "reviewComment",
                width: 200,
                render: (comment) => comment || "—",
              },
              {
                title: "Requested",
                dataIndex: "requestedAt",
                key: "requestedAt",
                render: (t) => (t ? moment(t).format("D MMM YYYY, h:mm A") : "—"),
              },
              {
                title: "Reviewed",
                dataIndex: "reviewedAt",
                key: "reviewedAt",
                render: (t) => (t ? moment(t).format("D MMM YYYY, h:mm A") : "—"),
              },
              {
                title: "Reviewed by",
                dataIndex: "reviewedBy",
                key: "reviewedBy",
                render: (v) => v || "—",
              },
            ]}
          />
        )}
      </div>
    </div>
  );

  const mainTabs = useMemo(() => {
    const tabs = [{ key: TAB_CALENDAR, label: "Calendar" }];
    if (canReviewAccessRequests) {
      tabs.push({
        key: TAB_ACCESS_REQUESTS,
        label: "Access requests",
        badgeCount: pendingAccessRequests.length,
      });
    }
    return tabs;
  }, [canReviewAccessRequests, pendingAccessRequests.length]);

  const eventPropGetter = (event) => {
    const statusLabel = event.statusTitle || event.title;
    if (event.className === "public-holiday-event" || event.resource?.holidayInfo) {
      return {
        style: {
          background: "#f3e8ff",
          border: "1px solid #9b6fd4",
          borderColor: "#9b6fd4",
          color: "#4a2d6b",
          fontSize: "13px",
          width: "95%",
          margin: "auto",
          fontWeight: "bold",
          cursor: "default",
        },
      };
    }
    if (statusLabel === "Missed") {
      return {
        style: {
          background: "#ff4f4f ",
          border: "1px solid #ff4f4f",
          borderColor: "#ff4f4f",
          color: "white",
          fontSize: "13px",
          width: "95%",
          margin: "auto",
          fontWeight: "bold",
        },
      };
    } else if (statusLabel === "Pending") {
      return {
        style: {
          background: "transparent",
          border: "1px solid var(--color-primary)",
          borderColor: "var(--color-primary)",
          color: "var(--color-primary)",
          fontSize: "13px",
          width: "95%",
          margin: "auto",
          fontWeight: "bold",
        },
      };
    } else if (statusLabel === "Filled") {
      return {
        style: {
          background: "var(--color-secondary) ",
          border: "1px solid var(--color-secondary)",
          borderColor: "var(--color-secondary)",
          color: "white",
          fontSize: "13px",
          width: "95%",
          margin: "auto",
          fontWeight: "bold",
        },
      };
    }
    return {};
  };

  const holidayDateCellWrapper = useCallback(
    ({ children, value }) => {
      const dateStr = moment(value).format("YYYY-MM-DD");
      const isHoliday =
        holidayDates.has(dateStr) ||
        isPublicHolidayDate(calendarPublicHolidaysByDate, dateStr);

      if (!isHoliday || !React.isValidElement(children)) {
        return children;
      }

      // Click handler lives on the child day cell — override it so slot
      // selection (and the missed-updates modal) never runs for holidays.
      return React.cloneElement(children, {
        className: `${children.props.className || ""} daily-hours-holiday-day`.trim(),
        onClick: (e) => {
          e.preventDefault();
          e.stopPropagation();
        },
        onSelect: undefined,
      });
    },
    [holidayDates, calendarPublicHolidaysByDate]
  );

  return (
    <div className="daily-hours-page">
      <header className="daily-hours-page__hero">
        <div className="daily-hours-page__intro">
          <h1 className="page-title">Daily Work Updates</h1>
          <p className="page-subtitle">
            Track work entries, review completion, and monitor daily productivity.
          </p>
        </div>
        {isAdminRole ? (
          <div className="daily-hours-page__hero-actions">
            <button
              type="button"
              className="daily-hours-page__view-sheet-btn"
              onClick={handleViewSheet}
              disabled={sheetOpening || viewingAllTeam}
              title={
                viewingAllTeam
                  ? "Select an employee to open their sheet"
                  : "Open this employee's Google Sheet"
              }
            >
              {sheetOpening ? "Opening…" : "View sheet"}
            </button>
          </div>
        ) : null}
      </header>
      <PageContainerHeader
        title="Daily Work Updates"
        selection={canPickEmployees && activeMainTab === TAB_CALENDAR}
        onSelectEmployee={onSelectEmployee}
        selectValue={selectedEmployee}
        defaultSelectValue={defaultEmployeeSelection}
        onClearSelection={clearEmployeeSelection}
        employeeSelectStaticOptions={
          canPickEmployees ? employeeSelectOptions : null
        }
        employeeSelectOptionsLoading={rosterLoading}
        employeeSelectExcludeCeoCmo
        employeeSelectAllowClear
      />
      {mainTabs.length > 1 ? (
        <div className="daily-hours-main-tabs">
          <div
            className="daily-hours-segmented-tabs"
            role="tablist"
            aria-label="Daily work updates sections"
          >
            {mainTabs.map((tab) => {
              const isActive = activeMainTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`daily-hours-segmented-tabs__item${isActive ? " is-active" : ""
                    }`}
                  onClick={() => setActiveMainTab(tab.key)}
                >
                  <span className="daily-hours-segmented-tabs__label">{tab.label}</span>
                  {tab.badgeCount > 0 ? (
                    <span className="daily-hours-segmented-tabs__badge">
                      {tab.badgeCount > 99 ? "99+" : tab.badgeCount}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
      {activeMainTab === TAB_ACCESS_REQUESTS ? (
        renderAccessRequestsPanel()
      ) : (
        <>
          {!isCalendarLoading && isOwnDailyHoursView && needsAccessRequest ? (
            <div className="daily-hours-pending-banner card-border" role="status">
              <p>
                You have pending{" "}
                <strong>{previousMonthLabel}</strong> daily work updates.
                {hasPendingAccessRequest
                  ? " Your access request is waiting for HR/Admin approval."
                  : " Request access from HR/Admin before filling those days or the current month."}
              </p>
              {!hasPendingAccessRequest ? (
                <Button
                  type="primary"
                  size="small"
                  onClick={() => openMonthlyAccessAlert(ACCESS_SCOPE_PREVIOUS)}
                >
                  Request Access
                </Button>
              ) : null}
            </div>
          ) : null}
          {!isCalendarLoading &&
          isOwnDailyHoursView &&
          needsCurrentMonthAccessRequest ? (
            <div className="daily-hours-pending-banner card-border" role="status">
              <p>
                You have missed{" "}
                <strong>{currentMonthLabel}</strong> daily work updates past the
                normal entry window.
                {hasPendingCurrentMonthAccessRequest
                  ? " Your access request is waiting for HR/Admin approval."
                  : " Request access from HR/Admin to fill those days mid-month."}
              </p>
              {!hasPendingCurrentMonthAccessRequest ? (
                <Button
                  type="primary"
                  size="small"
                  onClick={() => openMonthlyAccessAlert(ACCESS_SCOPE_CURRENT)}
                >
                  Request Access
                </Button>
              ) : null}
            </div>
          ) : null}
          {!isCalendarLoading && isOwnDailyHoursView && canEditPreviousMonth ? (
            <div className="daily-hours-approved-banner card-border" role="status">
              <p>
                Your request is <strong>approved</strong> for the requested{" "}
                <strong>{previousMonthLabel}</strong> dates. Fill them on the calendar below
                (click each <strong>Missed</strong> day). Current month stays locked until
                all pending days are done.
              </p>
              <Button type="primary" size="small" onClick={goToPreviousMonthOnCalendar}>
                Go to {previousMonthLabel}
              </Button>
            </div>
          ) : null}
          {!isCalendarLoading &&
          isOwnDailyHoursView &&
          canEditCurrentMonthMissed &&
          !hasPendingPreviousMonth ? (
            <div className="daily-hours-approved-banner card-border" role="status">
              <p>
                Your current-month access request is <strong>approved</strong> for the
                requested <strong>{currentMonthLabel}</strong> dates. Fill them on the
                calendar (click each <strong>Missed</strong> day). Any day missed after
                the request needs a new request.
              </p>
            </div>
          ) : null}
          <DailyWorkUpdatesStats
            calendarTasks={calendarTasks}
            calendarLeaveByDate={calendarLeaveByDate}
            publicHolidaysByDate={calendarPublicHolidaysByDate}
            monthDate={statsMonth}
            targetUserId={
              viewingAllTeam ? undefined : (selectedEmployee ?? user?.id)
            }
            teamUserIds={teamUserIds}
            isTeamView={viewingAllTeam}
            employeeName={selectedEmployeeName}
            loading={isCalendarLoading}
          />
          {isCalendarLoading ? (
            <div className="loading-indicator" style={{ minHeight: calendarHeight }}>
              Loading calendar data...
            </div>
          ) : (
            <div className="daily-hours-calendar-wrap card-border">
              <Calendar
                localizer={localizer}
                selectable={!viewingAllTeam}
                onSelectSlot={toggleHoursDrawer}
                events={calendarEvents}
                startAccessor="start"
                endAccessor="end"
                style={{ height: calendarHeight }}
                className="daily-hours-calendar"
                onSelectEvent={handleEventClick}
                views={["month"]}
                onNavigate={handleNavigate}
                date={currentDate}
                eventPropGetter={eventPropGetter}
                components={{
                  event: DailyHoursCalendarEvent,
                  dateCellWrapper: holidayDateCellWrapper,
                }}
              />
              {viewingAllTeam ? (
                <Tooltip
                  id={DAILY_HOURS_EVENT_NAMES_TOOLTIP_ID}
                  place="top"
                  className="daily-hours-event-names-tooltip"
                />
              ) : null}
            </div>
          )}
        </>
      )}
      {isDrawerOpen && (
        <ApplyHoursdrawer
          isOpen={isDrawerOpen}
          toggleHoursDrawer={toggleHoursDrawer}
          selectedDate={selectedDate}
          isReadOnly={isReadOnly}
          taskData={selectedTaskData}
          onMonthlyAccessBlocked={() => {
            setIsDrawerOpen(false);
            const dateStr = selectedDate
              ? moment(selectedDate).format("YYYY-MM-DD")
              : null;
            if (needsAccessRequest || isMonthlyBlocked) {
              openMonthlyAccessAlert(ACCESS_SCOPE_PREVIOUS);
            } else if (dateStr && shouldPromptCurrentMonthAccessForDate(dateStr)) {
              openMonthlyAccessAlert(ACCESS_SCOPE_CURRENT);
            }
          }}
        />
      )}
      <MonthlyAccessRequestModal
        open={accessAlertOpen}
        scope={accessRequestScope}
        initialStatus={monthlyAccessStatus}
        onClose={() => setAccessAlertOpen(false)}
        onGoToPreviousMonth={goToPreviousMonthOnCalendar}
        onStatusChange={handleMonthlyAccessStatusChange}
      />
      <Modal
        open={Boolean(accessReviewModal)}
        title={
          accessReviewModal?.status === "approved"
            ? "Approve access request"
            : "Reject access request"
        }
        onCancel={closeAccessReviewModal}
        okText={accessReviewModal?.status === "approved" ? "Approve" : "Reject"}
        okButtonProps={{
          danger: accessReviewModal?.status === "rejected",
          loading: reviewingAccessId === accessReviewModal?.requestId,
        }}
        onOk={() =>
          reviewAccessRequest(
            accessReviewModal?.requestId,
            accessReviewModal?.status,
            accessReviewComment
          )
        }
      >
        {accessReviewModal ? (
          <>
            <p>
              <strong>{accessReviewModal.employeeName}</strong>
            </p>
            <label className="daily-hours-access-reason-label" htmlFor="access-review-comment">
              Review comment
            </label>
            <Input.TextArea
              id="access-review-comment"
              rows={4}
              maxLength={ACCESS_REASON_MAX_LENGTH}
              showCount
              placeholder={
                accessReviewModal.status === "approved"
                  ? "Explain why this request is approved."
                  : "Explain why this request is rejected."
              }
              value={accessReviewComment}
              onChange={(e) => setAccessReviewComment(e.target.value)}
            />
          </>
        ) : null}
      </Modal>
    </div>
  );
};

export default DailyHoursPage;
